import React, { useState, useEffect } from 'react';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase/config';
import '../styles/login.css';

// Rate limiting configuration
const MAX_LOGIN_ATTEMPTS = 5;
const BAN_DURATION_MS = 30 * 60 * 1000; // 30 minutes

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Rate limiting states
  const [isBanned, setIsBanned] = useState(false);
  const [banTimeRemaining, setBanTimeRemaining] = useState(0);
  const [loginAttempts, setLoginAttempts] = useState(0);

  // Check rate limiting on mount
  useEffect(() => {
    checkRateLimit();
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);

  // Update ban timer every second
  useEffect(() => {
    if (isBanned && banTimeRemaining > 0) {
      const timer = setInterval(() => {
        const remaining = checkRateLimit();
        if (remaining <= 0) {
          setIsBanned(false);
          setBanTimeRemaining(0);
        }
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [isBanned, banTimeRemaining]);

  const checkRateLimit = () => {
    const banUntil = localStorage.getItem('loginBanUntil');
    const attempts = parseInt(localStorage.getItem('loginAttempts') || '0');
    
    setLoginAttempts(attempts);
    
    if (banUntil) {
      const banTime = parseInt(banUntil);
      const now = Date.now();
      const remaining = banTime - now;
      
      if (remaining > 0) {
        setIsBanned(true);
        setBanTimeRemaining(remaining);
        return remaining;
      } else {
        // Ban expired, reset
        localStorage.removeItem('loginBanUntil');
        localStorage.removeItem('loginAttempts');
        setIsBanned(false);
        setBanTimeRemaining(0);
        setLoginAttempts(0);
      }
    }
    return 0;
  };

  const incrementFailedAttempts = () => {
    const attempts = loginAttempts + 1;
    setLoginAttempts(attempts);
    localStorage.setItem('loginAttempts', attempts.toString());
    
    if (attempts >= MAX_LOGIN_ATTEMPTS) {
      const banUntil = Date.now() + BAN_DURATION_MS;
      localStorage.setItem('loginBanUntil', banUntil.toString());
      setIsBanned(true);
      setBanTimeRemaining(BAN_DURATION_MS);
      setError(`Too many failed attempts. Banned for 30 minutes.`);
    } else {
      setError(`Invalid credentials. ${MAX_LOGIN_ATTEMPTS - attempts} attempts remaining.`);
    }
  };

  const resetLoginAttempts = () => {
    localStorage.removeItem('loginAttempts');
    localStorage.removeItem('loginBanUntil');
    setLoginAttempts(0);
    setIsBanned(false);
    setBanTimeRemaining(0);
  };

  const formatBanTime = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Check if banned
    if (isBanned) {
      setError(`Account temporarily locked. Try again in ${formatBanTime(banTimeRemaining)}`);
      return;
    }

    setLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      
      // Success - reset attempts
      resetLoginAttempts();
      
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email);
      }
    } catch (err) {
      console.error('Login error:', err);
      
      // Increment failed attempts
      incrementFailedAttempts();
      
      if (err.code === 'auth/too-many-requests') {
        setError('Too many requests. Please wait a few minutes.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setResetLoading(true);

    // Validate email
    if (!resetEmail || !resetEmail.includes('@')) {
      setError('Please enter a valid email address');
      setResetLoading(false);
      return;
    }

    try {
      // Send password reset email with custom settings
      await sendPasswordResetEmail(auth, resetEmail, {
        url: window.location.origin,
        handleCodeInApp: false
      });
      
      setSuccess('✅ Password reset email sent! Check your inbox (and spam folder).');
      setResetEmail('');
      
      // Auto close after 5 seconds
      setTimeout(() => {
        setShowForgotPassword(false);
        setSuccess('');
      }, 5000);
    } catch (err) {
      console.error('Password reset error:', err);
      
      // Better error messages
      if (err.code === 'auth/user-not-found') {
        setError('No account found with this email address');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email format');
      } else if (err.code === 'auth/missing-email') {
        setError('Please enter an email address');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many requests. Please wait a few minutes.');
      } else {
        setError(`Failed to send reset email: ${err.message}`);
      }
    } finally {
      setResetLoading(false);
    }
  };

  React.useEffect(() => {
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);

  return (
    <div className="login-container">
      <div className="login-background">
        <div className="gradient-sphere gradient-sphere-1"></div>
        <div className="gradient-sphere gradient-sphere-2"></div>
        <div className="gradient-sphere gradient-sphere-3"></div>
      </div>

      <div className="login-box">
        <div className="login-header">
          <div className="login-logo">
            <div className="logo-icon">🏢</div>
          </div>
          <h1 className="login-title">Store Manager</h1>
          <p className="login-subtitle">Secure Admin Portal</p>
        </div>

        {!showForgotPassword ? (
          <form onSubmit={handleSubmit} className="login-form">
            {error && (
              <div className="alert alert-error">
                <span className="alert-icon">⚠️</span>
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div className="alert alert-success">
                <span className="alert-icon">✓</span>
                <span>{success}</span>
              </div>
            )}

            {isBanned && (
              <div className="alert alert-error">
                <span className="alert-icon">🔒</span>
                <span>Account locked. Retry in {formatBanTime(banTimeRemaining)}</span>
              </div>
            )}

            {!isBanned && loginAttempts > 0 && (
              <div className="alert alert-warning">
                <span className="alert-icon">⚠️</span>
                <span>{MAX_LOGIN_ATTEMPTS - loginAttempts} attempts remaining</span>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Email Address</label>
              <div className="input-wrapper">
                <input
                  type="email"
                  className="form-input"
                  placeholder="admin@storemanager.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Password</label>
              <div className="input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="form-input"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            <div className="form-options">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span>Remember me</span>
              </label>
              <button
                type="button"
                className="forgot-password-link"
                onClick={() => setShowForgotPassword(true)}
              >
                Forgot password?
              </button>
            </div>

            <button 
              type="submit" 
              className="login-btn"
              disabled={loading || isBanned}
            >
              {isBanned ? (
                <>
                  <span>🔒</span>
                  <span>Locked - {formatBanTime(banTimeRemaining)}</span>
                </>
              ) : loading ? (
                <>
                  <span className="spinner"></span>
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <span>🔐</span>
                  <span>Sign In</span>
                </>
              )}
            </button>
          </form>
        ) : (
          <form onSubmit={handleForgotPassword} className="login-form">
            <div className="forgot-password-header">
              <button
                type="button"
                className="back-button"
                onClick={() => {
                  setShowForgotPassword(false);
                  setError('');
                  setSuccess('');
                }}
              >
                ← Back to login
              </button>
              <h2 className="forgot-password-title">Reset Password</h2>
              <p className="forgot-password-subtitle">
                Enter your email and we'll send you a reset link
              </p>
            </div>

            {error && (
              <div className="alert alert-error">
                <span className="alert-icon">⚠️</span>
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div className="alert alert-success">
                <span className="alert-icon">✓</span>
                <span>{success}</span>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Email Address</label>
              <div className="input-wrapper">
                <input
                  type="email"
                  className="form-input"
                  placeholder="admin@storemanager.com"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  disabled={resetLoading}
                />
              </div>
            </div>

            <button 
              type="submit" 
              className="login-btn"
              disabled={resetLoading}
            >
              {resetLoading ? (
                <>
                  <span className="spinner"></span>
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <span>📧</span>
                  <span>Send Reset Link</span>
                </>
              )}
            </button>
          </form>
        )}

        <div className="login-footer">
          <div className="security-badge">
            <span className="badge-icon">🛡️</span>
            <div className="badge-text">
              <div className="badge-title">Bank-Level Security</div>
              <div className="badge-subtitle">256-bit AES encryption · 2FA protected</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;